import React from "react";
import {
  Alert,
  Button,
  Divider,
  Image,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { BulbOutlined } from "@ant-design/icons";

const { Paragraph, Text } = Typography;

export const neuroScoreColor = (score) => {
  const n = Number(score || 0);
  if (n >= 8) return "#52c41a";
  if (n >= 5) return "#faad14";
  return "#ff4d4f";
};

const AD_TYPES = [
  {
    key: "topOfFunnel",
    label: "Top of Funnel",
    tip: "Awareness & discovery ads. Optimized for grabbing attention and creating emotional first impressions with cold audiences who don't know your brand yet.",
  },
  {
    key: "brandAwareness",
    label: "Brand",
    tip: "Brand-building ads. Optimized for memory encoding and brand recall. Makes viewers remember your brand name, visual identity, and core message.",
  },
  {
    key: "directResponse",
    label: "Direct Response",
    tip: "Conversion-focused ads. Optimized for decision-making brain regions. Drives immediate action with clear urgency and compelling offers.",
  },
  {
    key: "bottomFunnel",
    label: "Bottom Funnel",
    tip: "Close-the-sale ads for warm audiences. Optimized for low cognitive load and strong decision triggers. Removes friction to convert ready buyers.",
  },
  {
    key: "retargeting",
    label: "Retargeting",
    tip: "Re-engagement ads for people who've seen you before. Optimized for recognition memory and familiarity processing. Triggers 'I know this' brain response.",
  },
  {
    key: "ugcSocial",
    label: "UGC / Social",
    tip: "User-generated content style. Optimized for authenticity signals — high emotional, low 'ad detection' frontal response. Feels real, not salesy.",
  },
];

/**
 * The 10/10 variation image on its own, so callers can show it outside a
 * collapsed section while the rest of the report stays folded away.
 */
export function OptimalNeuroImage({
  image,
  loading = false,
  error = "",
  predicted = null,
  showPredicted = true,
}) {
  if (!image && !loading && !error) return null;
  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      {showPredicted && predicted?.to ? (
        <Space size={[6, 4]} wrap>
          <Tag color="orange">{predicted.from}/10</Tag>
          <Text type="secondary">{"→"}</Text>
          <Tag color="green">{predicted.to}/10</Tag>
          {predicted.confidence ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({predicted.confidence} confidence)
            </Text>
          ) : null}
        </Space>
      ) : null}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: 24,
            background: "rgba(148, 163, 184, 0.08)",
            borderRadius: 8,
          }}
        >
          <Spin />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Generating image from the perfect prompt with GPT Image 2{"…"}
          </Text>
        </div>
      ) : image ? (
        <Image
          src={image}
          alt="GPT-generated 10/10 neuro-optimized creative"
          style={{ borderRadius: 8, width: "100%" }}
        />
      ) : error ? (
        <Alert
          type="warning"
          showIcon
          message="Image generation failed"
          description={error}
          style={{ fontSize: 12 }}
        />
      ) : null}
    </Space>
  );
}

/**
 * The neuro analysis read-out: score, dimensions, funnel fit, improvements,
 * brain scans and the 10/10 prompt. Shared by the Analysis panel's Neuro tab
 * and the bulk Neuro tab so both render an identical report.
 */
export default function NeuroAnalysisResult({
  result,
  fullResult,
  optimalImage = null,
  optimalImageLoading = false,
  optimalImageError = "",
  onGenerateOptimalImage,
  showOptimalImage = true,
  showOptimalPrompt = true,
  showBrainScans = true,
}) {
  if (!result) return null;

  const optimalPrompt = fullResult?.optimalPrompt || null;
  const tribeResult = fullResult?.tribeResult || null;

  return (
    <Space
      direction="vertical"
      size="small"
      style={{ width: "100%", overflow: "hidden" }}
    >
      {/* Score + Summary */}
      <div
        style={{
          background: "rgba(148, 163, 184, 0.08)",
          borderRadius: 4,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div style={{ textAlign: "center", minWidth: 60 }}>
          <div
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: neuroScoreColor(result.overallNeuroScore),
              lineHeight: 1,
            }}
          >
            {result.overallNeuroScore}
          </div>
          <Text type="secondary" style={{ fontSize: 10 }}>
            / 10
          </Text>
        </div>
        <div style={{ flex: 1 }}>
          <Paragraph style={{ marginBottom: 0, fontSize: 13 }}>
            {result.summary || "No summary returned."}
          </Paragraph>
        </div>
      </div>

      {/* Dimension score bar */}
      <div style={{ display: "flex", gap: 4 }}>
        {[
          { label: "Attention", score: result.attentionAnalysis?.score },
          { label: "Emotion", score: result.emotionalResponse?.score },
          { label: "Memory", score: result.memoryEncoding?.score },
          { label: "Cognitive", score: result.cognitiveLoad?.score },
          { label: "Decision", score: result.decisionTriggers?.score },
        ].map((dim) => (
          <div
            key={dim.label}
            style={{
              flex: 1,
              textAlign: "center",
              padding: "6px 2px",
              background: "rgba(148, 163, 184, 0.06)",
              borderRadius: 4,
            }}
          >
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: neuroScoreColor(dim.score),
              }}
            >
              {dim.score || 0}
            </div>
            <Text
              type="secondary"
              style={{
                fontSize: 9,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {dim.label}
            </Text>
          </div>
        ))}
      </div>

      {/* Ad Type / Funnel Fit */}
      {result.adTypeScores ? (
        <div
          style={{
            background: "rgba(148, 163, 184, 0.06)",
            borderRadius: 4,
            padding: "10px 12px",
          }}
        >
          <Text
            strong
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              display: "block",
              marginBottom: 8,
            }}
          >
            Best Use
          </Text>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {AD_TYPES.map(({ key, label, tip }) => {
              const data = result.adTypeScores[key];
              const score = data?.score || 0;
              const color =
                score >= 8
                  ? "#52c41a"
                  : score >= 6
                    ? "#faad14"
                    : score >= 4
                      ? "#d48806"
                      : "#ff4d4f";
              return (
                <Tooltip
                  key={key}
                  title={
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {label} — {score}/10
                      </div>
                      <div>{tip}</div>
                      {data?.reason ? (
                        <div style={{ marginTop: 4, fontStyle: "italic" }}>
                          {data.reason}
                        </div>
                      ) : null}
                    </div>
                  }
                  placement="top"
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 10px",
                      borderRadius: 4,
                      cursor: "help",
                      border: `2px solid ${score >= 7 ? color : "rgba(148,163,184,0.2)"}`,
                      background: score >= 7 ? `${color}11` : "transparent",
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 700, color }}>
                      {score}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 500 }}>{label}</span>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Improvement Suggestions */}
      {result.improvementSuggestions?.length > 0 ? (
        <div
          style={{
            background: "rgba(148, 163, 184, 0.06)",
            borderRadius: 4,
            padding: "10px 12px",
          }}
        >
          <Text
            strong
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              display: "block",
              marginBottom: 8,
            }}
          >
            How to Improve
          </Text>
          {result.improvementSuggestions.slice(0, 4).map((item, i) => (
            <div
              key={i}
              style={{
                marginBottom: 12,
                paddingBottom: 10,
                borderBottom:
                  i < Math.min(result.improvementSuggestions.length, 4) - 1
                    ? "1px solid var(--app-border)"
                    : "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 6,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--brand-500)",
                    minWidth: 18,
                  }}
                >
                  #{i + 1}
                </span>
                <Text strong style={{ fontSize: 13 }}>
                  {item?.area || "Improvement"}
                </Text>
              </div>
              {item?.current_issue || item?.currentIssue ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--app-muted)",
                    marginBottom: 3,
                    paddingLeft: 24,
                  }}
                >
                  <Text strong style={{ fontSize: 12 }}>
                    Issue:{" "}
                  </Text>
                  {item.current_issue || item.currentIssue}
                </div>
              ) : null}
              {item?.neuroscience_basis || item?.neuroscienceBasis ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--app-muted)",
                    marginBottom: 3,
                    paddingLeft: 24,
                  }}
                >
                  <Text strong style={{ fontSize: 12 }}>
                    Brain science:{" "}
                  </Text>
                  {item.neuroscience_basis || item.neuroscienceBasis}
                </div>
              ) : null}
              {item?.specific_change || item?.specificChange ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--brand-500)",
                    fontWeight: 500,
                    marginBottom: 3,
                    paddingLeft: 24,
                  }}
                >
                  <Text
                    strong
                    style={{ fontSize: 12, color: "var(--brand-500)" }}
                  >
                    Fix:{" "}
                  </Text>
                  {item.specific_change || item.specificChange}
                </div>
              ) : null}
              {item?.predicted_impact || item?.predictedImpact ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--app-muted)",
                    paddingLeft: 24,
                  }}
                >
                  <Text strong style={{ fontSize: 12 }}>
                    Impact:{" "}
                  </Text>
                  {item.predicted_impact || item.predictedImpact}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* Brain Scan Images */}
      {showBrainScans &&
      (tribeResult?.overview || tribeResult?.timestepImages?.length > 0) ? (
        <div
          style={{
            background: "rgba(148, 163, 184, 0.06)",
            borderRadius: 4,
            padding: "10px 12px",
          }}
        >
          <Text
            strong
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              display: "block",
              marginBottom: 8,
            }}
          >
            Brain Scans
          </Text>
          {tribeResult.overview ? (
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <Image
                src={`data:${tribeResult.overview.mimeType || "image/png"};base64,${tribeResult.overview.base64}`}
                alt="Brain activation overview"
                style={{ maxWidth: "100%", maxHeight: 280, borderRadius: 4 }}
                preview={{ mask: "View full scan" }}
              />
            </div>
          ) : null}
          {tribeResult.timestepImages?.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 6,
              }}
            >
              {tribeResult.timestepImages.map((img, i) => (
                <div
                  key={i}
                  style={{
                    textAlign: "center",
                    background: "#000",
                    borderRadius: 4,
                    overflow: "hidden",
                    padding: 3,
                  }}
                >
                  <Image
                    src={`data:${img.mimeType || "image/png"};base64,${img.base64}`}
                    alt={`t=${img.timestep}s`}
                    style={{ maxWidth: "100%", borderRadius: 3 }}
                    preview={{ mask: `t=${img.timestep}s` }}
                  />
                  <Text style={{ fontSize: 9, color: "#888", display: "block" }}>
                    t={img.timestep}s
                  </Text>
                </div>
              ))}
            </div>
          ) : null}
          <Text
            type="secondary"
            style={{ fontSize: 10, display: "block", marginTop: 6 }}
          >
            Meta Tribe v2 fMRI predictions — real brain activation data
          </Text>
        </div>
      ) : null}
      {tribeResult?.error ? (
        <Alert
          type="warning"
          showIcon
          message="Brain scan unavailable"
          description={tribeResult.error}
          style={{ fontSize: 12 }}
        />
      ) : null}
      {fullResult?.gptError ? (
        <Alert
          type="warning"
          showIcon
          message={`GPT analysis failed: ${fullResult.gptError}`}
          style={{ fontSize: 12 }}
        />
      ) : null}
      {fullResult?.geminiError ? (
        <Alert
          type="warning"
          showIcon
          message={`Gemini analysis failed: ${fullResult.geminiError}`}
          style={{ fontSize: 12 }}
        />
      ) : null}

      {/* 10/10 Optimal Prompt */}
      {showOptimalPrompt && optimalPrompt?.prompt ? (
        <>
          <Divider orientation="left" style={{ margin: "8px 0" }}>
            <BulbOutlined /> Generate 10/10 Neuro Score Variation
          </Divider>
          <div
            style={{
              background: "rgba(24, 144, 255, 0.06)",
              borderRadius: 8,
              padding: "10px 14px",
              border: "1px solid rgba(24, 144, 255, 0.15)",
            }}
          >
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              {optimalPrompt.predictedScoreImprovement?.to ? (
                <Space size={[6, 4]} wrap>
                  <Tag color="orange">
                    {optimalPrompt.predictedScoreImprovement.from}/10
                  </Tag>
                  <Text type="secondary">{"→"}</Text>
                  <Tag color="green">
                    {optimalPrompt.predictedScoreImprovement.to}/10
                  </Tag>
                  {optimalPrompt.predictedScoreImprovement.confidence ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({optimalPrompt.predictedScoreImprovement.confidence}{" "}
                      confidence)
                    </Text>
                  ) : null}
                </Space>
              ) : null}
              {showOptimalImage ? (
                <OptimalNeuroImage
                  image={optimalImage}
                  loading={optimalImageLoading}
                  error={optimalImageError}
                  showPredicted={false}
                />
              ) : null}
              <div
                style={{
                  background: "rgba(255,255,255,0.5)",
                  borderRadius: 6,
                  padding: "8px 10px",
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {optimalPrompt.prompt}
              </div>
              {optimalPrompt.keyNeuroTechniques?.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {optimalPrompt.keyNeuroTechniques.map((t, i) => (
                    <Tag key={i} color="purple" style={{ fontSize: 11 }}>
                      {String(t)}
                    </Tag>
                  ))}
                </div>
              ) : null}
              {optimalPrompt.rationale ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {optimalPrompt.rationale}
                </Text>
              ) : null}
              <Space size={8} wrap>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    navigator.clipboard?.writeText(optimalPrompt.prompt);
                    message.success("Optimal prompt copied to clipboard");
                  }}
                >
                  Copy Prompt
                </Button>
                {onGenerateOptimalImage ? (
                  <Button
                    size="small"
                    icon={<BulbOutlined />}
                    loading={optimalImageLoading}
                    onClick={() => onGenerateOptimalImage()}
                  >
                    {optimalImage ? "Regenerate Image" : "Generate Image"}
                  </Button>
                ) : null}
              </Space>
            </Space>
          </div>
        </>
      ) : null}
    </Space>
  );
}
